import {Button, Tree, Row, Col, Modal, Form, Input, Menu, Dropdown, Tooltip, Space, InputNumber, Select, Spin } from 'antd';
import { apiClient } from '../../api_actions/index';
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { MinusOutlined, PlusOutlined, MoreOutlined  } from '@ant-design/icons';
import humanizeString from '../../utils'
import './HardwareInfo.scss';
import LoadingOrbitalSpinner from '../../components/LoadingOrbitalSpinner/LoadingOrbitalSpinner';
import { REGEX_PATTERNS } from '../../constants/enums';

// Define form fields configuration
// We dont use key, dataIndex in the traditional sense since the tree rendering is custom. Name is used to fill in form values right now
const formFieldsConfig = {
  hardware: [
    { name: 'hardware_info_id', label: 'Hardware ID', type: 'input', disabled: true},
    { name: 'external_ip', label: 'External IP', type: 'ip', rules: [{ pattern: REGEX_PATTERNS.IPV4, message: 'Please enter a valid IPv4 address' }] },
    { name: 'internal_ip', label: 'Internal IP', type: 'ip', rules: [{ pattern: REGEX_PATTERNS.IPV4, message: 'Please enter a valid IPv4 address' }] },
    { name: 'hardware_type_id', label: 'Hardware Type', type: 'select', 
      optionsEndpoint: 'read/hardwaretypes', 
      optionsLabel: ['hardware_make', 'hardware_model'], 
      labelSeparator: ' - ',
      optionsValue: 'hardware_type_id',
      searchable: true,
      rules: [{ required: true }]
    },
    { name: 'owner_id', label: 'Owner', type: 'select',
      optionsEndpoint: 'read/hardwareowners',
      optionsLabel: ['owner_name'],
      optionsValue: 'owner_id',
      searchable: true,
      rules: [{ required: true }]
    },
    { name: 'tag', label: 'Tag', type: 'input', rules: [{ pattern: REGEX_PATTERNS.TAG_CHARS, message: 'Only letters, numbers, spaces, and common punctuation allowed' }] },
    { name: 'parent_id', label: 'Parent ID', type: 'input', disabled: true }
  ],
  stream: [
    { name: 'id', label: 'Stream ID', type: 'input', disabled: true},
    { name: 'endpoint', label: 'Endpoint', type: 'endpoint' },
    { name: 'name', label: 'Name', type: 'input',  rules: [{ pattern: REGEX_PATTERNS.GENERAL_INPUT, message: 'Only letters, numbers, spaces, and common punctuation allowed'}]},
    { name: 'data_type_id', label: 'Data Type', type: 'select',
      optionsEndpoint: 'read/datatypes',
      optionsLabel: ['name'],
      optionsValue: 'data_type_id',
      searchable: true,
      rules: [{ required: true }]
    },
    { name: 'stream_resolution_id', label: 'Stream Resolution', type: 'select', key: ['stream_resolution_id'],
      optionsEndpoint: 'read/streamresolutions',
      optionsLabel: ['name'],
      optionsValue: 'stream_resolution_id',
      searchable: true,
      rules: [{ required: true }]
    },
    { name: 'hardware_info_id', label: 'Hardware ID', type: 'select', disabled: true,
      optionsValue: 'hardware_info_id',
      optionsLabel: 'hardware_info_id'
     }
  ]
};

// Helper: Build tree data for AntD Tree with nested stream items
const buildTreeData = (datum, data, handleAddHardwareChild, handleAddStream, handleDeleteHardware, handleDeleteStream) => {
  const map = {};
  
  data.forEach(p => {
    const menu = (
      <Menu>
        <Menu.Item 
          key="add-hardware" 
          icon={<PlusOutlined />}
          onClick={(e) => {
            handleAddHardwareChild(p);
            e.domEvent.stopPropagation()
          }}
        >
          Add hardware child
        </Menu.Item>
        <Menu.Item 
          key="add-stream" 
          icon={<PlusOutlined style={{ color: '#52c41a' }} />}
          onClick={(e) => {
            handleAddStream(p.hardware_info_id);
            e.domEvent.stopPropagation()
          }}
        >
          Add stream
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item 
          key="remove" 
          icon={<MinusOutlined />}
          danger
          onClick={(e) => {
            handleDeleteHardware(p); 
            e.domEvent.stopPropagation()
          }}
        >
          Remove hardware
        </Menu.Item>
      </Menu>
    );

    map[p.hardware_info_id] = { 
      key: `hw-${p.hardware_info_id}`, 
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{`${humanizeString(p.tag ?? "")} | ${humanizeString(p.hardware_type.hardware_model)} | ${humanizeString(p.internal_ip) ?? ""}`}</span>
          <Dropdown overlay={menu} trigger={['click']}>
            <Button 
              type="text" 
              size="small"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>
      ),
      children: [],
      data: p,
      type: 'hardware',
    };
    
    // Add related items as children if they exist
    if (p.streams && p.streams.length > 0) {
      p.streams.forEach(stream => {

        const streamMenu = (
          <Menu>
            <Menu.Item 
              key="remove" 
              icon={<MinusOutlined />}
              danger
              onClick={(e) => {
                handleDeleteStream(stream);
                e.domEvent.stopPropagation()
              }}
            >
              Remove stream
            </Menu.Item>
          </Menu>
        );

        map[p.hardware_info_id].children.push({
          key: `stream-${stream.id}`,
          title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ color: '#1890ff', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-all' }}>
                {`${humanizeString(stream.endpoint ?? "")}`}
              </span>
              <Dropdown overlay={streamMenu} trigger={['click']}>
                <Button 
                  type="text" 
                  size="small"
                  icon={<MoreOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  style={{ flexShrink: 0 }}
                />
              </Dropdown>
            </div>
          ),
          data: stream,
          type: 'stream',
          isLeaf: true,
        });
      });
    }
  });
  
  // Build hierarchy for hardware items
  const roots = [];
  data.forEach(p => {
    if (datum.parent_id && datum.parent_id !== p.hardware_info_id && map[p.parent_id]) {
      map[p.parent_id].children.push(map[p.hardware_info_id]);
    } else {
      roots.push(map[p.hardware_info_id]);
    }
  });
  
  return roots;
};

// Helper function to render form field based on configuration
const renderFormField = (field, fieldOptions, form) => {
  const commonProps = {
    disabled: field.disabled,
    placeholder: field.placeholder
  };

  switch (field.type) {
    case 'select':
      return (
        <Select
          {...commonProps}
          showSearch={field.searchable}
          filterOption={(input, option) =>
            option.label?.toLowerCase().includes(input.toLowerCase())
          }
          options={fieldOptions[field.name] || []}
        />
      );
    case 'number':
      return <InputNumber {...commonProps} style={{ width: '100%' }} />;
    case 'ip':
      return (
        <Input
          {...commonProps}
          onChange={(e) => {
            const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_IP_CHARS, '');
            form.setFieldValue(field.name, sanitized);
          }}
        />
      );
    case 'endpoint':
      return (
        <Input
          {...commonProps}
          onChange={(e) => {
            const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_ENDPOINT_CHARS, '');
            form.setFieldValue(field.name, sanitized);
          }}
        />
      );
    default:
      return (
        <Input
          {...commonProps}
          onChange={(e) => {
            const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_GENERAL_INPUT, '');
            form.setFieldValue(field.name, sanitized);
          }}
        />
      );
  }
};

// Helper function to generate hardware display string for consistent formatting and sorting
const getHardwareDisplayString = (datum) => {
  return `${humanizeString(datum.tag) ?? ""} | ${humanizeString(datum.hardware_type.hardware_model)} | ${humanizeString(datum.hardware_type.hardware_make)}`;
};

export default function HardwareInfoPage() {
  const endpoint = "read/hardwareinfo"
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDatum, setSelectedDatum] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldOptions, setFieldOptions] = useState({});
  const [form] = Form.useForm();
  
  const handleNodeClick = (nodeData, type) => {
    setSelectedDatum(nodeData);
    setSelectedType(type);
    
    // Automatically extract IDs from nested relational objects
    const formValues = { ...nodeData };
    const fields = formFieldsConfig[type] || [];
    
    fields.forEach(field => {
      if (field.type === 'select' && field.optionsValue) {
        // Nested relationship check relies on the PK being TABLE_NAME_id, which is pursuant to our DB design but bad practice, so this should be changed possibly in along with the field options to be more dynamic -RH
        const fieldBaseName = field.name.replace(/_id$/, ''); // Remove '_id' suffix

        if (nodeData[fieldBaseName] && typeof nodeData[fieldBaseName] === 'object') {
          // Extract the ID from the nested object
          formValues[field.name] = nodeData[fieldBaseName][field.optionsValue];
        }
      }
    });
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleAddRoot = () => {
    setSelectedDatum(null);
    setSelectedType('hardware');
    form.resetFields();
    setModalVisible(true);
  };

  const handleAddStream = (hardwareId) => {
    setSelectedDatum({ hardware_info_id: hardwareId });
    setSelectedType("stream");
    form.resetFields();
    form.setFieldsValue({ hardware_info_id: hardwareId });
    setModalVisible(true);
  };

  const handleAddHardwareChild = (hardware_info) => {
    setSelectedDatum({ parent_id: hardware_info.hardware_info_id });
    setSelectedType('hardware');
    form.resetFields();
    form.setFieldsValue({ parent_id: hardware_info.hardware_info_id });
    setModalVisible(true);
  };

  const handleDeleteHardware = (hardware_info) => {
    setSelectedDatum(hardware_info);
    setSelectedType('hardware');
    form.resetFields();
    form.setFieldsValue(hardware_info);
    handleDelete(hardware_info)
    loadData()
  }

  const handleDeleteStream = (stream) => {
    setSelectedDatum(stream);
    setSelectedType('stream');
    form.resetFields();
    form.setFieldsValue(stream);
    handleDelete(stream)
    loadData()
  }

  useEffect(() => {
    loadFieldOptions()
    loadData();
  }, [endpoint]);

  const loadFieldOptions = async () => {
    // Get all fields that need options from both hardware and stream configs
    const allFields = [...formFieldsConfig.hardware, ...formFieldsConfig.stream];
    const fieldsNeedingOptions = allFields.filter(field => field.optionsEndpoint);
    
    if (fieldsNeedingOptions.length === 0) return;
    
    try {
      const optionsPromises = fieldsNeedingOptions.map(async (field) => {
        const result = await apiClient.post(field.optionsEndpoint);
        
        const options = result.data.map(item => {
          let label;
          
          // Check if optionsLabel is an array (multiple fields)
          if (Array.isArray(field.optionsLabel)) {
            label = field.optionsLabel
              .map(fieldName => item[fieldName])
              .filter(val => val)
              .join(field.labelSeparator || ' - ');
          } else {
            label = item[field.optionsLabel || 'name'];
          }
          
          return {
            label: label,
            value: item[field.optionsValue || 'id'],
          };
        });
        
        return { fieldName: field.name, options };
      });

      const optionsResults = await Promise.all(optionsPromises);
      
      const optionsMap = {};
      optionsResults.forEach(({ fieldName, options }) => {
        optionsMap[fieldName] = options;
      });
      
      setFieldOptions(optionsMap);
    } catch (error) {
      console.error('Failed to load field options:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {    
      const result = await apiClient.post(endpoint);
      setData(result.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
    setLoading(false);
  };

  const handleDrop = (info, data, setData) => {
    // Only allow dragging hardware items, not streams
    if (!info.dragNode.key.startsWith('hw-') || !info.node.key.startsWith('hw-')) {
      return;
    }
    
    const dragKey = info.dragNode.key.replace('hw-', '');
    const dropKey = info.node.key.replace('hw-', '');

    // Update parent_id in hardware data
    const updated = data.map(p => {
      if (p.hardware_info_id === dragKey) {
        return { ...p, parent_id: dropKey };
      }
      return p;
    });
    setData(updated);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (selectedType === 'hardware') {
        if (selectedDatum && selectedDatum.hardware_info_id) {
          // Update existing hardware
          await apiClient.post('update/hardwareinfo', values);
        } else {
          // Create new root hardware
          //values.hardware_info_id == 
          await apiClient.post('create/hardwareinfo', values);
        }
      } else if (selectedType === 'stream') {
        if (selectedDatum && selectedDatum.id) {
          // Update existing stream item
          await apiClient.post('update/streams', values);
        } else {
          // Create new stream item
          await apiClient.post('create/streams', values);
        }
      }
      
      setModalVisible(false);
      await loadData(); // Reload data after save
    } catch (error) {
      console.error('Failed to save:', error);
      // Optionally show error message to user
    }
  };

  const handleDelete = (selectedDatum) => {
    if (selectedDatum && selectedDatum.id) {
      apiClient.post("delete/streams",selectedDatum)
      }

    //} else if (selectedType === "hardware") {
      if (selectedDatum && selectedDatum.hardware_info_id) {
        apiClient.post("delete/hardwareinfo", selectedDatum)
      }
    //}
  }

  return (
    <Spin spinning={loading} tip="Loading data..." fullscreen={loading} indicator={<LoadingOrbitalSpinner />}>
      <div className='container-wrapper' style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddRoot}
          >
            Add Root
          </Button>
        </div>
      
      <Row gutter={[32, 32]}>
        {data.filter(datum => datum.hardware_info_id === datum.parent_id).sort((a, b) => 
          getHardwareDisplayString(a).localeCompare(getHardwareDisplayString(b), undefined, { numeric: true, sensitivity: 'base' })
        ).map(datum => {
          if (datum.hardware_info_id == datum.parent_id){
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={datum.hardware_info_id}>
                <h3>{getHardwareDisplayString(datum)}</h3>
                <Tree
                  treeData={buildTreeData(datum, data, handleAddHardwareChild, handleAddStream, handleDeleteHardware, handleDeleteStream)}
                  //draggable
                  blockNode
                  //defaultExpandAll
                  indent={4}
                  onSelect={(keys, info) => handleNodeClick(info.node.data, info.node.type)}
                  onDrop={(info) => handleDrop(info, data, setData)}
                />
              </Col>
            );
          }
          return null;
        })}
      </Row>
      <Modal
        title={
          selectedDatum && selectedDatum.hardware_info_id && selectedType === 'hardware'
            ? `Edit ${selectedDatum.hardware_type?.hardware_model}`
            : selectedDatum && selectedType === 'stream' && selectedDatum.id
            ? 'Edit Stream Item'
            : selectedType === 'hardware' && selectedDatum && selectedDatum.parent_id
            ? 'Add Hardware Child'
            : selectedType === 'hardware'
            ? 'Add New Root Hardware'
            : 'Add New Stream Item'
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
      >
        <Form form={form} layout="vertical">
          {selectedType && formFieldsConfig[selectedType]?.map(field => (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={field.rules}
            >
              {renderFormField(field, fieldOptions, form)}
            </Form.Item>
          ))}
        </Form>
      </Modal>
      </div>
    </Spin>
  );
}